import { useEffect, useRef, RefObject } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ページ遷移時にスクロール位置をトップにリセットするカスタムフック
 *
 * @param containerRef - スクロールコンテナのRef
 * @param options - オプション設定
 * @returns containerRef - スクロールコンテナのRef（渡されなかった場合は新規作成）
 *
 * @example
 * ```tsx
 * // 方法1: 新しいrefを作成（特定のスクロールコンテナ用）
 * const contentRef = useScrollToTop<HTMLDivElement>();
 * return <div ref={contentRef}>...</div>;
 *
 * // 方法2: 既存のrefを使用
 * const myRef = useRef<HTMLDivElement>(null);
 * useScrollToTop(myRef);
 * return <div ref={myRef}>...</div>;
 *
 * // 方法3: windowスクロールをリセット
 * useScrollToTopWindow();
 * ```
 */
export function useScrollToTop<T extends HTMLElement>(
  containerRef?: RefObject<T | null>,
  options?: {
    /** スクロールリセットを無効にするパスパターン（正規表現） */
    excludePatterns?: RegExp[];
    /** スクロール動作 */
    behavior?: ScrollBehavior;
  }
): RefObject<T | null> {
  const internalRef = useRef<T>(null);
  const ref = containerRef ?? internalRef;
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    // パスが変更されていない場合はスキップ（ハッシュ変更のみの場合など）
    if (previousPathname.current === location.pathname) {
      return;
    }

    // 除外パターンに一致する場合はスキップ
    if (options?.excludePatterns?.some(pattern => pattern.test(location.pathname))) {
      previousPathname.current = location.pathname;
      return;
    }

    // スクロール位置をリセット
    if (ref.current) {
      ref.current.scrollTo({
        top: 0,
        left: 0,
        behavior: options?.behavior ?? 'instant',
      });
    }

    previousPathname.current = location.pathname;
  }, [location.pathname, options?.excludePatterns, options?.behavior, ref]);

  return ref;
}

/**
 * ページ遷移時にwindowのスクロール位置をトップにリセットするカスタムフック
 * Layout全体がスクロールするパターン用
 *
 * @param options - オプション設定
 *
 * @example
 * ```tsx
 * useScrollToTopWindow();
 * ```
 */
export function useScrollToTopWindow(
  options?: {
    /** スクロールリセットを無効にするパスパターン（正規表現） */
    excludePatterns?: RegExp[];
    /** スクロール動作 */
    behavior?: ScrollBehavior;
  }
): void {
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    // パスが変更されていない場合はスキップ（ハッシュ変更のみの場合など）
    if (previousPathname.current === location.pathname) {
      return;
    }

    // 除外パターンに一致する場合はスキップ
    if (options?.excludePatterns?.some(pattern => pattern.test(location.pathname))) {
      previousPathname.current = location.pathname;
      return;
    }

    // windowのスクロール位置をリセット
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: options?.behavior ?? 'instant',
    });

    previousPathname.current = location.pathname;
  }, [location.pathname, options?.excludePatterns, options?.behavior]);
}
